import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DailyBanner from "@/components/DailyBanner";
import { CheckCircle2, XCircle, Lock } from "lucide-react";
import SponsorBadge from "@/components/SponsorBadge";

export default function Challenges() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/challenges")
      .then((r) => setList(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 py-16" data-testid="challenges-page">
      <div className="flex items-end justify-between mb-12">
        <div>
          <div className="label-mono mb-3">Catalog</div>
          <h1 className="font-display font-black text-4xl md:text-6xl tracking-tighter uppercase">
            All Episodes
          </h1>
        </div>
        {!user && (
          <Link to="/register" className="btn btn-primary hidden sm:inline-flex" data-testid="challenges-signup-cta">
            Sign up to play
          </Link>
        )}
      </div>

      <div className="mb-12">
        <DailyBanner />
      </div>

      {loading ? (
        <div className="label-mono">Loading episodes…</div>
      ) : list.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="label-mono mb-3">No episodes yet</div>
          <div style={{ color: "var(--text-dim)" }}>Check back soon — new challenges drop weekly.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {list.map((c, i) => (
            <Link
              to={`/challenges/${c.id}`}
              key={c.id}
              className="card p-8 group transition-all hover:-translate-y-1 slide-up"
              style={{ animationDelay: `${i * 0.05}s` }}
              data-testid={`challenge-card-${c.id}`}
            >
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="tag">{c.category}</span>
                  {c.sponsor && <SponsorBadge sponsor={c.sponsor} compact />}
                </div>
                {c.user_vote ? (
                  c.user_correct ? (
                    <span className="tag tag-correct flex items-center gap-1.5">
                      <CheckCircle2 size={12} /> Correct
                    </span>
                  ) : (
                    <span className="tag tag-wrong flex items-center gap-1.5">
                      <XCircle size={12} /> Missed
                    </span>
                  )
                ) : (
                  <span className="tag flex items-center gap-1.5">
                    <Lock size={11} /> Unvoted
                  </span>
                )}
              </div>
              <h3 className="font-display font-bold text-2xl mb-3 tracking-tight">{c.title}</h3>
              {c.hook && (
                <p className="text-sm leading-relaxed mb-6" style={{ color: "var(--text-dim)" }}>
                  {c.hook}
                </p>
              )}
              <div className="flex items-center gap-4 label-mono">
                <span><span style={{ color: "var(--ai)" }}>●</span> {c.votes_a + c.votes_b} votes</span>
                <span className="ml-auto group-hover:text-white transition-colors">Open →</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
